import { Fragment } from 'react';
import PropTypes from 'prop-types';
import { marked } from 'marked';

import {
  decodeHtmlEntities,
  formatFeedbackReferenceLabel,
  stripIncompleteFeedbackToken,
  tokenizeFeedbackReferences,
} from 'utlis/feedbackReferences';

import styles from './styles.module.scss';

// Configure marked for safe rendering
marked.setOptions({
  breaks: true,
  gfm: true,
});

const showText = value => decodeHtmlEntities(value || '');

const renderReferenceText = (text, keyPrefix, onNavigateToFeedback) => {
  if (!text) {
    return null;
  }

  return tokenizeFeedbackReferences(text).map((segment, index) => {
    const key = `${keyPrefix}-${index}`;

    if (segment.type === 'text') {
      return (
        <Fragment key={key}>
          {showText(segment.value)}
        </Fragment>
      );
    }

    const { label, feedbackId } = segment.value;

    return (
      <button
        key={key}
        type="button"
        className={styles['feedback-reference-button']}
        aria-label={`Open ${showText(label)} finding ${feedbackId}`}
        onClick={() => onNavigateToFeedback?.(feedbackId)}
      >
        {formatFeedbackReferenceLabel(showText(label), feedbackId)}
      </button>
    );
  });
};

function renderTokens(tokens, keyPrefix, onNavigateToFeedback) {
  if (!tokens?.length) {
    return null;
  }

  return tokens.map((token, index) => renderToken(
    token,
    `${keyPrefix}-${index}`,
    onNavigateToFeedback
  ));
}

function renderToken(token, key, onNavigateToFeedback) {
  switch (token.type) {
    case 'paragraph':
      return (
        <p key={key}>
          {renderTokens(token.tokens, key, onNavigateToFeedback)}
        </p>
      );
    case 'text':
      if (token.tokens) {
        return (
          <Fragment key={key}>
            {renderTokens(token.tokens, key, onNavigateToFeedback)}
          </Fragment>
        );
      }

      return (
        <Fragment key={key}>
          {renderReferenceText(token.text, key, onNavigateToFeedback)}
        </Fragment>
      );
    case 'strong':
      return (
        <strong key={key}>
          {renderTokens(token.tokens, key, onNavigateToFeedback)}
        </strong>
      );
    case 'em':
      return (
        <em key={key}>
          {renderTokens(token.tokens, key, onNavigateToFeedback)}
        </em>
      );
    case 'del':
      return (
        <del key={key}>
          {renderTokens(token.tokens, key, onNavigateToFeedback)}
        </del>
      );
    case 'link':
      return (
        <a key={key} href={token.href}>
          {token.tokens
            ? renderTokens(token.tokens, key, onNavigateToFeedback)
            : showText(token.text)}
        </a>
      );
    case 'codespan':
      return (
        <code key={key}>
          {showText(token.text)}
        </code>
      );
    case 'code':
      return (
        <pre key={key}>
          <code>
            {showText(token.text)}
          </code>
        </pre>
      );
    case 'br':
      return <br key={key} />;
    case 'hr':
      return <hr key={key} />;
    case 'space':
      return null;
    case 'heading': {
      const HeadingTag = `h${Math.min(token.depth || 1, 4)}`;

      return (
        <HeadingTag key={key}>
          {renderTokens(token.tokens, key, onNavigateToFeedback)}
        </HeadingTag>
      );
    }
    case 'blockquote':
      return (
        <blockquote key={key}>
          {renderTokens(token.tokens, key, onNavigateToFeedback)}
        </blockquote>
      );
    case 'list': {
      const ListTag = token.ordered ? 'ol' : 'ul';

      return (
        <ListTag key={key}>
          {token.items.map((item, index) => (
            <li key={`${key}-${index}`}>
              {renderTokens(item.tokens, `${key}-${index}`, onNavigateToFeedback)}
            </li>
          ))}
        </ListTag>
      );
    }
    case 'html':
      return (
        <Fragment key={key}>
          {showText(token.text)}
        </Fragment>
      );
    default:
      if (token.tokens) {
        return (
          <Fragment key={key}>
            {renderTokens(token.tokens, key, onNavigateToFeedback)}
          </Fragment>
        );
      }

      return token.text
        ? (
          <Fragment key={key}>
            {showText(token.text)}
          </Fragment>
        )
        : null;
  }
}

const ChatMessageContent = ({ content, isStreaming = false, onNavigateToFeedback }) => {
  const source = isStreaming ? stripIncompleteFeedbackToken(content) : content;
  const visibleContent = decodeHtmlEntities(source);

  if (!visibleContent) {
    return null;
  }

  return renderTokens(marked.lexer(visibleContent), 'chat', onNavigateToFeedback);
};

ChatMessageContent.propTypes = {
  content: PropTypes.string,
  isStreaming: PropTypes.bool,
  onNavigateToFeedback: PropTypes.func,
};

export default ChatMessageContent;
